import {
  BookingSubscriptionsPlan,
  DashboardInstallationAddress,
  DeviceRoles,
  HardwareBundle,
  SubscriptionPlan,
  SubscriptionStatus,
} from '@vi-iot/client';

import { Platform } from '../installation-details';

export interface BookingSubscription {
  id: number;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  subscriptionDate: Date;
  subscribedBy: string;
  subscribedByName: string;
  expirationDate: Date;
  boilerId: string;
  ownerName: string;
  marketingName: string;
  deviceImageUrl: string;
  installationLocation: string;
  trial: boolean;
  registeredInstallation: boolean;
  installationId?: number;
  gatewaySerial?: string;
  originalBundleInvoice?: string;
  hardwareBundle?: HardwareBundle;
  voucher?: string;
}

export enum BOOKING_SUBSCRIPTIONS_FILTER_TYPE {
  ALL = 'All',
  PRO = 'Pro',
  PLUS = 'Plus',
  TRIAL = 'Trial',
  AUTOMATIC_EXTENSION = 'Automatic extension',
}

export enum BOOKING_TRIAL_SUBSCRIPTIONS_FILTER_TYPE {
  ALL = 'All',
  ONLINE = 'Online devices',
  OFFLINE = 'Offline devices',
}

export enum BOOKING_TABS {
  SELECT_PLAN = '0',
  SELECT_DEVICE = '1',
  CHECKOUT = '2',
}

export enum FLAT_RATE_BOOKING_TABS {
  SELECT_PLAN = '0',
  CHECKOUT = '1',
}

export enum VOUCHER_BOOKING_TABS {
  SELECT_PLAN = '0',
  SELECT_DEVICE = '1',
  ACTIVATE_CODE = '2',
}

export enum COMMERCIAL_BOOKING_TABS {
  SELECT_PLAN = 0,
  CHECKOUT = 1,
}

export enum BOOKING_CONTINGENT_SUBSCRIPTION_TABS {
  SELECT_DEVICE = '0',
  CHECKOUT = '1',
}

export enum BOOKING_PARAMS {
  INVOICE_NUMBER = 'invoiceNumber',
  BOILER_SERIAL = 'boilerSerial',
}
export enum CANCELING_STATUS {
  START = 'Start',
  CANCELING = 'Canceling',
  SUCCESS = 'Success',
  FAILED = 'Failed',
}

export enum BOOKING_TRIAL_STATUS {
  LOADED_SUCCESS = 'loaded-success',
  LOADED_ERROR = 'loaded-error',
}

export interface ContingentSubscriptionsQueryParams {
  invoiceNumber: string;
}

export interface SelectedInstallationsPlatforms {
  e3Devices: BookingDashboardDevice[];
  legacyDevices: BookingDashboardDevice[];
}

export interface BookingSubscriptionsPlanByNames {
  plus: BookingSubscriptionsPlan;
  pro: BookingSubscriptionsPlan;
  plusLegacy: BookingSubscriptionsPlan;
  proLegacy: BookingSubscriptionsPlan;
  basic: BookingSubscriptionsPlan;
}

export interface BookingDataForPriceCalculation {
  installations: SelectedInstallationsPlatforms;
  plans: BookingSubscriptionsPlanByNames;
}

export interface BookingQueryParams {
  selectedStep?: BOOKING_TABS;
  boilerSerial?: string;
  update?: boolean;
  platform?: Platform;
  installationId?: number;
}

export interface Whitelist {
  isWhitelisted: (deviceSerial: string) => boolean;
}

export enum BookingPlansLoadingState {
  Loaded,
  Loading,
  Error,
  Other,
}

export enum VoucherValidationResponse {
  Required = 'required',
  Invalid = 'invalid',
  AlreadyUsed = 'alreadyUsed',
  Valid = 'valid',
}

export interface BookingDashboardDevice {
  vin: string;
  roles: DeviceRoles[];
  marketingName: string;
  platform: Platform;
  plan: SubscriptionPlan;
  installationId: number;
  ownerName: string;
  canBeBooked: boolean;
  canBeBundled: boolean;
  isVoucherAvailable: boolean;
  address: DashboardInstallationAddress;
  location: string;
  isMultiGateway: boolean;
}

export type PartialBookingDashboardDevice = Omit<
  BookingDashboardDevice,
  'canBeBooked' | 'canBeBundled' | 'isVoucherAvailable'
>;

export interface BookingPagination {
  page: number;
  pageSize: number;
}

export const DEFAULT_BOOKING_PAGE_SIZE = 25;
